import type { CSSProperties } from 'react';
import Icon, { type IconName } from '@/components/Icon';
import { useLocations } from '@/stores/locations';

/** The card's glyphs, in the order `CardFacts` draws them. Fixed: they are the application's. */
const GLYPHS: readonly (readonly [IconName, string])[] = [
  ['incall', 'In-call'],
  ['outcall', 'Out-call'],
  ['phone', 'Call'],
  ['message', 'Text'],
  ['mail', 'Email'],
];

/**
 * What the marks on a card mean: the glyphs, which are compiled in, and the colour packs, which
 * are the admin's and arrive with the city rail (`useLocations`). A pack is a square of its colour
 * and its name, the same colour its cards are outlined in. Until the packs have loaded, or when
 * the admin has defined none, that half is simply absent rather than an empty heading.
 *
 * Printed twice — at the foot of the menu and in the footer — and nothing in it is a control.
 */
export default function Legend({ variant }: { variant: 'footer' | 'drawer' }) {
  const packs = useLocations((state) => state.packs);

  return (
    <section className={`ee-legend ee-legend--${variant}`} aria-label="Key">
      <p className="ee-legend__eyebrow">Key</p>
      <ul className="ee-legend__list">
        {GLYPHS.map(([icon, word]) => (
          <li className="ee-legend__item" key={icon}>
            <Icon name={icon} size={20} className="ee-legend__glyph" />
            {word}
          </li>
        ))}
      </ul>
      {packs.length > 0 ? (
        <ul className="ee-legend__list" aria-label="Colour packs">
          {packs.map((pack) => (
            <li className="ee-legend__item" key={pack.name}>
              <span
                className="ee-legend__swatch"
                style={{ '--ee-pack': pack.colour } as CSSProperties}
                aria-hidden="true"
              />
              {pack.name}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
